import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import ModuleUnitsPanel from 'pages/ModuleUnitsPanel';
import ModuleDiploma from './ModuleDiploma';
import DiplomaDownload from './DiplomaDownload';
import {
  ChevronDown, ChevronRight, ArrowRight, BookOpen, FileText,
  CheckCircle, Circle, Lock, Loader2, Award, X,
  ShieldAlert, Video, VideoOff, MessageSquare, Send,
  Eye, Headphones, Type, Zap, Sparkles, Brain,
   Play, Pause, Square, Image as ImageIcon
} from 'lucide-react';

import DOMPurify from 'dompurify';

/* ─── GLOBAL STYLES — colores 100% via CSS variables ──────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  
  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dash-root { 
    font-family: var(--org-font, 'DM Sans', sans-serif);
    min-height: 100vh;
    background-color: var(--org-bg, #060810);
    background-image: var(--brand-bg-image, url('/bg-dashboard.png'));
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
    color: #ffffff;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, var(--org-scan, rgba(0,255,255,0.4)), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content { position: relative; z-index: 2; }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }
  .glass-panel-inner {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
  }

  /* Glows dinámicos */
  .glow-a1 { box-shadow: 0 0 20px color-mix(in srgb, var(--org-accent, #00FFFF) 10%, transparent); border-color: color-mix(in srgb, var(--org-accent, #00FFFF) 25%, transparent); }
  .glow-a1:hover { box-shadow: 0 0 30px color-mix(in srgb, var(--org-accent, #00FFFF) 18%, transparent); border-color: color-mix(in srgb, var(--org-accent, #00FFFF) 45%, transparent); }
  .glow-a2 { box-shadow: 0 0 20px color-mix(in srgb, var(--org-accent2, #FF00FF) 10%, transparent); border-color: color-mix(in srgb, var(--org-accent2, #FF00FF) 25%, transparent); }
  .glow-a2:hover { box-shadow: 0 0 30px color-mix(in srgb, var(--org-accent2, #FF00FF) 18%, transparent); border-color: color-mix(in srgb, var(--org-accent2, #FF00FF) 45%, transparent); }
  .glow-a3 { box-shadow: 0 0 20px color-mix(in srgb, var(--org-accent3, #ccff00) 10%, transparent); border-color: color-mix(in srgb, var(--org-accent3, #ccff00) 25%, transparent); }

  /* Botones neon dinámicos */
  .btn-neon-a1 {
    background: var(--org-accent, #00FFFF); color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px color-mix(in srgb, var(--org-accent, #00FFFF) 50%, transparent);
    transition: all 0.2s;
  }
  .btn-neon-a1:hover:not(:disabled) {
    filter: brightness(1.15);
    box-shadow: 0 0 25px color-mix(in srgb, var(--org-accent, #00FFFF) 70%, transparent);
    transform: translateY(-1px);
  }
  .btn-neon-a1:disabled { background: color-mix(in srgb, var(--org-accent, #00FFFF) 20%, transparent); color: rgba(255,255,255,0.3); box-shadow: none; cursor: not-allowed; }
  .test-option:has(input:focus-visible) {
  outline: 2px solid var(--org-accent3, #ccff00);
  outline-offset: 2px;
}
  .btn-neon-a2 {
    background: var(--org-accent2, #FF00FF); color: #fff; font-weight: 800; border: none;
    box-shadow: 0 0 15px color-mix(in srgb, var(--org-accent2, #FF00FF) 50%, transparent);
    transition: all 0.2s;
  }
  .btn-neon-a2:hover:not(:disabled) {
    filter: brightness(1.15);
    box-shadow: 0 0 25px color-mix(in srgb, var(--org-accent2, #FF00FF) 70%, transparent);
    transform: translateY(-1px);
  }
  .btn-neon-a2:disabled { background: color-mix(in srgb, var(--org-accent2, #FF00FF) 20%, transparent); color: rgba(255,255,255,0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-a3 {
    background: var(--org-accent3, #ccff00); color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px color-mix(in srgb, var(--org-accent3, #ccff00) 50%, transparent);
    transition: all 0.2s;
  }
  .btn-neon-a3:hover:not(:disabled) {
    filter: brightness(1.12);
    box-shadow: 0 0 25px color-mix(in srgb, var(--org-accent3, #ccff00) 70%, transparent);
    transform: translateY(-1px);
  }
  .btn-neon-a3:disabled { background: color-mix(in srgb, var(--org-accent3, #ccff00) 20%, transparent); color: rgba(255,255,255,0.3); box-shadow: none; cursor: not-allowed; }

  .cy-input {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    color: #fff; transition: all 0.3s;
  }
  .cy-input:focus {
    border-color: var(--org-accent, #00FFFF);
    box-shadow: 0 0 15px color-mix(in srgb, var(--org-accent, #00FFFF) 25%, transparent);
    outline: none;
  }

  .markdown-content h1, .markdown-content h2, .markdown-content h3 { color: #fff; }
  .markdown-content p, .markdown-content li { color: rgba(255,255,255,0.8); }
  .markdown-content strong { color: var(--org-accent, #00FFFF); font-weight: 700; }
  .markdown-content em { color: var(--org-accent2, #FF00FF); }
  .markdown-content h2 { border-bottom: 1px solid color-mix(in srgb, var(--org-accent, #00FFFF) 30%, transparent); padding-bottom: 6px; }

  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: color-mix(in srgb, var(--org-accent, #00FFFF) 35%, transparent); }
`;

// ─── helpers de color inline ──────────────────────────────────────────────────
const a1bg   = (op = '15%') => `color-mix(in srgb, var(--org-accent,  #00FFFF) ${op}, transparent)`;
const a1bd   = (op = '30%') => `color-mix(in srgb, var(--org-accent,  #00FFFF) ${op}, transparent)`;
const a2bg   = (op = '15%') => `color-mix(in srgb, var(--org-accent2, #FF00FF) ${op}, transparent)`;
const a2bd   = (op = '30%') => `color-mix(in srgb, var(--org-accent2, #FF00FF) ${op}, transparent)`;
const a3bg   = (op = '15%') => `color-mix(in srgb, var(--org-accent3, #ccff00) ${op}, transparent)`;
const a3bd   = (op = '30%') => `color-mix(in srgb, var(--org-accent3, #ccff00) ${op}, transparent)`;

// ─── Learning Style Config ────────────────────────────────────────────────────
const STYLE_CONFIG = {
  visual:      { label: 'Visual',      icon: Eye,       tip: 'Contenido organizado con esquemas visuales y estructura clara' },
  auditory:    { label: 'Auditivo',    icon: Headphones, tip: 'Explicaciones narrativas adaptadas para asimilar escuchando' },
  reading:     { label: 'Lectura',     icon: Type,      tip: 'Texto detallado con definiciones y estructura académica' },
  kinesthetic: { label: 'Kinestésico', icon: Zap,       tip: 'Ejemplos prácticos y ejercicios aplicados al mundo real' },
};
const useLearningStyle = (user) => {
  const valid = ['auditory', 'reading', 'kinesthetic'];
  const get = () => {
    try {
      const s = localStorage.getItem('user');
      if (s) {
        const parsed = JSON.parse(s);
        if (valid.includes(parsed.learning_style)) return parsed.learning_style;
      }
    } catch (_) {}
    return valid.includes(user?.learning_style) ? user.learning_style : 'auditory';  };
  const [style, setStyle] = useState(get);

  // Reaccionar cuando cambia el objeto user directamente
  useEffect(() => {
    if (valid.includes(user?.learning_style)) {
      setStyle(user.learning_style);
    }
  }, [user?.learning_style]);

  // Polling localStorage por si se actualiza desde Settings
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const s = localStorage.getItem('user');
        if (s) {
          const parsed = JSON.parse(s);
          if (valid.includes(parsed.learning_style)) {
            setStyle(prev => parsed.learning_style !== prev ? parsed.learning_style : prev);
          }
        }
      } catch (_) {}
    }, 1500);
    return () => clearInterval(id);
  }, []);

  return style;
};

const useAdaptedContent = (content, sectionTitle, learningStyle, api) => {
  const [adapted, setAdapted] = useState(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});
  useEffect(() => {
    if (!content || !learningStyle || learningStyle === 'visual') { setAdapted(null); return; }
    const key = `${sectionTitle}__${learningStyle}`;
    if (cacheRef.current[key]) { setAdapted(cacheRef.current[key]); return; }
    let cancelled = false;
    setLoading(true); setAdapted(null);

    api.post('/ai/generate', {
      task: 'adapt',
      style: learningStyle,
      title: sectionTitle || '',
      content: content.slice(0, 3000),
    })
      .then(({ data }) => {
        if (!cancelled && data.response) {
          cacheRef.current[key] = data.response;
          setAdapted(data.response);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; setLoading(false); };
  }, [content, sectionTitle, learningStyle]);
  return { adapted, loading };
};

const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
const randomizeTest = (test) => ({ ...test, questions: shuffle(test.questions).map(q => ({ ...q, options: shuffle(q.options) })) });

const ANTI_CHEAT_CSS = `
  .test-active-zone, .test-active-zone *, .roleplay-active-zone, .roleplay-active-zone * {
    user-select: none !important; -webkit-user-select: none !important;
  }
  @media print { .test-active-zone, .roleplay-active-zone { visibility: hidden !important; } }
`;

const useScreenRecording = ({ active, testId, moduleId, api }) => {
  const mediaRecorderRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null);
  const [recording, setRecording] = useState(false); const [permission, setPermission] = useState('pending');
  useEffect(() => { if (!active || !testId) return; startRecording(); return () => { stopAndUpload(); }; }, [active, testId]);
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 5, width: 1280 }, audio: false });
      streamRef.current = stream; setPermission('granted');
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm' });
      mediaRecorderRef.current = recorder; chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      stream.getVideoTracks()[0].onended = () => { toast.error('⚠️ Dejaste de compartir la pantalla'); setRecording(false); setPermission('denied'); };
      recorder.start(5000); setRecording(true); toast.success('🔴 Grabación iniciada', { duration: 4000 });
    } catch { setPermission('denied'); }
  };
  const stopAndUpload = async () => {
    if (!mediaRecorderRef.current) return;
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      recorder.onstop = async () => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: 'video/webm' });
          if (blob.size > 0 && testId && moduleId) {
            try { const form = new FormData(); form.append('recording', blob, `test-${testId}-${Date.now()}.webm`); form.append('test_id', testId); form.append('module_id', moduleId); await api.post(`/modules/${moduleId}/test-recordings`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
          }
        }
        mediaRecorderRef.current = null; chunksRef.current = []; setRecording(false); resolve();
      };
      if (recorder.state !== 'inactive') recorder.stop(); else resolve();
    });
  };
  return { recording, permission, stopAndUpload };
};

const MarkdownContent = ({ content }) => {
  if (!content) return <p className="text-white/50 italic">Sin contenido disponible.</p>;
  const lines = content.split('\n'); const els = []; let listItems = []; let k = 0;
  const flush = () => { if (!listItems.length) return; els.push(<ul key={k++} className="list-disc list-inside space-y-1 mb-4 ml-2">{listItems.map((it, i) => <li key={i}>{it}</li>)}</ul>); listItems = []; };
  lines.forEach(line => {
    if (/^###\s/.test(line))       { flush(); els.push(<h3 key={k++} className="text-lg font-bold mt-6 mb-2">{line.slice(4)}</h3>); }
    else if (/^##\s/.test(line))   { flush(); els.push(<h2 key={k++} className="text-xl font-bold mt-8 mb-3 pb-2 border-b-2">{line.slice(3)}</h2>); }
    else if (/^#\s/.test(line))    { flush(); els.push(<h1 key={k++} className="text-2xl font-bold mt-4 mb-4">{line.slice(2)}</h1>); }
    else if (/^\d+\.\s/.test(line)) listItems.push(line.replaceAll(/^\d+\.\s/g, ''));
    else if (/^[-*]\s/.test(line))  listItems.push(line.slice(2));
    else if (!line.trim())         { flush(); els.push(<div key={k++} className="h-3" />); }
    else { flush(); const html = DOMPurify.sanitize(
  line.replaceAll(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replaceAll(/\*(.+?)\*/g, '<em>$1</em>'),
  { ALLOWED_TAGS: ['strong', 'em'], ALLOWED_ATTR: [] }
); els.push(<p key={k++} className="leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />); }
  });
  flush();
  return <div className="markdown-content prose-invert max-w-none">{els}</div>;
};

const LearningStyleBanner = ({ style }) => {
  const cfg = STYLE_CONFIG[style] || STYLE_CONFIG.visual;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm mb-4"
      style={{ background: a1bg('8%'), borderColor: a1bd('25%'), color: 'var(--org-accent, #00FFFF)' }}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div>
        <span className="font-bold tracking-wider uppercase text-[11px]">Modo {cfg.label}</span>
        <span className="ml-2 font-medium opacity-80">— {cfg.tip}</span>
      </div>
    </div>
  );
};

const useTTS = () => {
  const [speaking, setSpeaking] = useState(false); const [paused, setPaused] = useState(false);
  const [supported] = useState(() => 'speechSynthesis' in window); const utteranceRef = useRef(null);
  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  const speak = (text) => {
    if (!supported) { toast.error('Tu navegador no soporta Text-to-Speech'); return; }
    window.speechSynthesis.cancel();
    const clean = text.replaceAll(/#{1,6}\s/g, '').replaceAll(/\*\*(.+?)\*\*/g, '$1').replaceAll(/\*(.+?)\*/g, '$1').replaceAll(/^[-*]\s/gm, '').replaceAll(/^\d+\.\s/gm, '').slice(0, 5000);
    const utter = new SpeechSynthesisUtterance(clean);
    utter.lang = 'es-ES'; utter.rate = 0.92; utter.pitch = 1.05;
    const voices = window.speechSynthesis.getVoices();
    const esVoice = voices.find(v => v.lang.startsWith('es') && v.localService) || voices.find(v => v.lang.startsWith('es'));
    if (esVoice) utter.voice = esVoice;
    utter.onstart = () => { setSpeaking(true); setPaused(false); };
    utter.onend   = () => { setSpeaking(false); setPaused(false); };
    utter.onerror = () => { setSpeaking(false); setPaused(false); };
    utteranceRef.current = utter; window.speechSynthesis.speak(utter);
  };
  const pause  = () => { window.speechSynthesis.pause(); setPaused(true); };
  const resume = () => { window.speechSynthesis.resume(); setPaused(false); };
  const stop   = () => { window.speechSynthesis.cancel(); setSpeaking(false); setPaused(false); };
  return { speak, pause, resume, stop, speaking, paused, supported };
};

const useVisualDiagram = (content, sectionTitle, learningStyle) => {
  const { api } = useAuth();
  const [svg, setSvg] = useState(null); const [loading, setLoading] = useState(false); const cacheRef = useRef({});
  const generate = async () => {
    if (!content || learningStyle !== 'visual') return;
    const key = `diagram__${sectionTitle}`;
    if (cacheRef.current[key]) { setSvg(cacheRef.current[key]); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/ai/generate', {
        task: 'diagram', title: sectionTitle, content: content.slice(0, 2000),
      });
      const svgMatch = data.response?.match(/<svg[\s\S]*<\/svg>/i);
      if (svgMatch) { cacheRef.current[key] = svgMatch[0]; setSvg(svgMatch[0]); }
    } catch {} finally { setLoading(false); }
  };
  return { svg, loading, generate };
};

const TTSControls = ({ content }) => {
  const { speak, pause, resume, stop, speaking, paused, supported } = useTTS();
  if (!supported) return null;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl mb-4"
      style={{ background: a2bg('8%'), border: `1px solid ${a2bd('25%')}` }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: a2bg('20%'), border: `1px solid ${a2bd('40%')}` }}>
        <Headphones className="w-4 h-4" style={{ color: 'var(--org-accent2, #FF00FF)' }} />
      </div>
      <span className="text-[11px] uppercase tracking-widest font-bold flex-1" style={{ color: 'var(--org-accent2, #FF00FF)' }}>Escuchar Contenido</span>
      {!speaking ? (
        <button onClick={() => speak(content)} className="btn-neon-a2 px-4 py-1.5 text-xs rounded-lg flex items-center gap-2">
          <Play className="w-3 h-3" /> Reproducir
        </button>
      ) : (
        <>
          {paused ? (
            <button onClick={resume} className="btn-neon-a2 px-4 py-1.5 text-xs rounded-lg flex items-center gap-2"><Play className="w-3 h-3" /> Reanudar</button>
          ) : (
            <button onClick={pause} className="border px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--org-accent2, #FF00FF)', borderColor: a2bd('50%') }}>
              <Pause className="w-3 h-3" /> Pausar
            </button>
          )}
          <button onClick={stop} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/50 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2">
            <Square className="w-3 h-3" />
          </button>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider ml-2" style={{ color: 'var(--org-accent2, #FF00FF)' }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--org-accent2, #FF00FF)', boxShadow: '0 0 8px var(--org-accent2, #FF00FF)' }} />
            {paused ? 'Pausado' : 'Reproduciendo'}
          </span>
        </>
      )}
    </div>
  );
};

const VisualDiagramPanel = ({ content, sectionTitle }) => {
  const { svg, loading, generate } = useVisualDiagram(content, sectionTitle, 'visual');
  const [shown, setShown] = useState(false);
  return (
    <div className="mb-4">
      {!shown ? (
        <button onClick={() => { setShown(true); generate(); }} className="btn-neon-a1 w-full justify-center rounded-xl text-sm flex items-center gap-2 px-4 py-3">
          <ImageIcon className="w-4 h-4" /> Generar diagrama visual con IA <Sparkles className="w-4 h-4 ml-1" />
        </button>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${a1bd('30%')}`, background: 'rgba(0,0,0,0.4)' }}>
          <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: a1bd('20%'), background: a1bg('5%') }}>
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--org-accent, #00FFFF)' }}>
              <ImageIcon className="w-3.5 h-3.5" /> Diagrama visual — Generado por IA
            </span>
            <button onClick={() => setShown(false)} className="text-white/40 hover:text-white text-xs uppercase font-bold tracking-widest transition-colors">Ocultar</button>
          </div>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3" style={{ color: 'var(--org-accent, #00FFFF)' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ filter: 'drop-shadow(0 0 8px var(--org-accent, #00FFFF))' }} />
              <p className="text-xs uppercase tracking-widest font-bold">Generando holograma...</p>
            </div>
          ) : svg ? (
            <div className="p-3 flex justify-center bg-[#0a0a0a]"
  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true } }) }} />
          ) : (
            <div className="py-8 text-center text-white/40 text-sm">No se pudo generar el diagrama.</div>
          )}
        </div>
      )}
    </div>
  );
};

const ContentModal = ({ section, isCompleted, onClose, onMarkComplete, marking, learningStyle, api }) => {
  const { adapted, loading } = useAdaptedContent(section.content, section.title, learningStyle, api);
  const cfg = STYLE_CONFIG[learningStyle] || STYLE_CONFIG.visual;
  const Icon = cfg.icon;
  const displayContent = adapted || section.content;
useEffect(() => () => window.speechSynthesis?.cancel(), []);
useEffect(() => {
  const onKey = (e) => { if (e.key === 'Escape') onClose(); };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
}, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 dash-root" style={{ minHeight: '100vh' }}>
      <style>{GLOBAL_STYLES}</style>
      <button type="button" tabIndex={-1} aria-label="Cerrar" onClick={onClose}
  className="absolute inset-0 w-full h-full bg-black/60 backdrop-blur-md cursor-default" />
<div role="dialog" aria-modal="true" aria-label={section.title}
  className="w-full max-w-3xl bg-[#060810]/95 backdrop-blur-xl flex flex-col shadow-2xl rounded-2xl overflow-hidden relative z-10 h-[90vh] sm:h-auto sm:max-h-[90vh]"
        style={{ border: `1px solid ${a1bd('30%')}`, boxShadow: `0 0 40px ${a1bg('15%')}` }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ background: `linear-gradient(90deg, ${a1bg('10%')}, transparent)`, borderColor: a1bd('20%') }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: a1bg('20%'), border: `1px solid ${a1bd('40%')}` }}>
              <BookOpen className="w-5 h-5" style={{ color: 'var(--org-accent, #00FFFF)' }} />
            </div>
            <h2 className="text-lg font-black text-white tracking-tight">{section.title}</h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1 transition-colors"><X className="w-6 h-6" /></button>
        </div>
        {/* Badge adaptación */}
        <div className="px-6 pt-5 pb-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest border mb-4"
            style={{ background: a1bg('8%'), borderColor: a1bd('25%'), color: 'var(--org-accent, #00FFFF)' }}>
            <Icon className="w-3.5 h-3.5" />
            Adaptado: {cfg.label}
            {loading && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
            {adapted && !loading && <Sparkles className="w-3 h-3 ml-1" />}
          </div>
          {loading && <p className="text-[11px] mb-3 flex items-center gap-2 uppercase tracking-widest font-bold" style={{ color: 'var(--org-accent, #00FFFF)' }}><Brain className="w-4 h-4 animate-pulse" /> La IA está procesando...</p>}
          {learningStyle === 'auditory' && !loading && displayContent && <TTSControls content={displayContent} />}
          {learningStyle === 'visual' && !loading && displayContent && <VisualDiagramPanel content={displayContent} sectionTitle={section.title} />}
        </div>
        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          {loading ? (
            <div className="space-y-4 animate-pulse pt-4">
              {[...Array(6)].map((_, i) => <div key={i} className={`h-3 bg-white/10 rounded ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`} />)}
            </div>
          ) : displayContent ? <MarkdownContent content={displayContent} /> : (
            <div className="text-center py-16 text-white/30"><BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="uppercase tracking-widest text-xs font-bold">Sin contenido</p></div>
          )}
        </div>
        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 bg-black/40 flex items-center justify-between gap-4">
          {isCompleted ? (
            <>
              <span className="flex items-center gap-2 font-black uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-md border"
                style={{ color: 'var(--org-accent3, #ccff00)', background: a3bg('10%'), borderColor: a3bd('30%') }}>
                <CheckCircle className="w-4 h-4" /> Completada
              </span>
              <button onClick={onClose} className="btn-neon-a1 px-6 py-2 rounded-lg text-xs">Cerrar Visualizador</button>
            </>
          ) : (
            <>
              <p className="text-[10px] text-white/50 uppercase tracking-widest font-bold hidden sm:block">Lee el contenido para desbloquear el test</p>
              <button onClick={onMarkComplete} disabled={marking} className="w-full sm:w-auto flex items-center justify-center gap-2 btn-neon-a3 px-6 py-3 rounded-xl text-xs sm:ml-auto">
                {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Marcar como completado
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TestStartModal = ({ test, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 dash-root">
    <style>{GLOBAL_STYLES}</style>
    <div className="bg-[#0a0a0a] rounded-2xl max-w-md w-full overflow-hidden relative z-10"
      style={{ border: `1px solid ${a2bd('30%')}`, boxShadow: `0 0 40px ${a2bg('15%')}` }}>
      <div className="border-b px-6 py-5 text-white flex items-center gap-4"
        style={{ background: `linear-gradient(90deg, ${a2bg('20%')}, transparent)`, borderColor: a2bd('20%') }}>
        <div className="p-3 rounded-xl" style={{ background: a2bg('20%'), border: `1px solid ${a2bd('40%')}` }}>
          <ShieldAlert className="w-6 h-6" style={{ color: 'var(--org-accent2, #FF00FF)' }} />
        </div>
        <div>
          <h2 className="text-xl font-black tracking-tight">¿Iniciar Test?</h2>
          <p className="text-white/60 text-xs uppercase tracking-widest font-bold mt-1">{test.section_title}</p>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
          <Video className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
          <div><p className="font-black text-orange-400 text-xs uppercase tracking-widest mb-1">Grabación requerida</p><p className="text-white/60 text-xs leading-relaxed">Al iniciar se solicitará acceso a tu pantalla para supervisión anti-fraude.</p></div>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: a1bg('10%'), border: `1px solid ${a1bd('30%')}` }}>
          <Lock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--org-accent, #00FFFF)' }} />
          <div><p className="font-black text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--org-accent, #00FFFF)' }}>Entorno Seguro</p><p className="text-white/60 text-xs leading-relaxed">Preguntas aleatorizadas. Copiar/pegar bloqueado.</p></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="text-center bg-white/5 border border-white/10 rounded-xl p-3"><p className="font-black text-2xl text-white">{test.questions.length}</p><p className="text-[9px] uppercase tracking-widest font-bold text-white/40 mt-1">Preguntas</p></div>
          <div className="text-center rounded-xl p-3" style={{ background: a3bg('10%'), border: `1px solid ${a3bd('30%')}` }}>
            <p className="font-black text-2xl" style={{ color: 'var(--org-accent3, #ccff00)' }}>70%</p>
            <p className="text-[9px] uppercase tracking-widest font-bold mt-1" style={{ color: 'color-mix(in srgb, var(--org-accent3, #ccff00) 60%, transparent)' }}>Para Aprobar</p>
          </div>
        </div>
      </div>
      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel} className="flex-1 px-4 py-3 border border-white/20 text-white hover:bg-white/10 rounded-xl font-bold text-xs uppercase tracking-widest transition-all">Cancelar</button>
        <button onClick={onConfirm} className="flex-1 btn-neon-a2 rounded-xl text-xs uppercase tracking-widest flex items-center justify-center gap-2"><Play className="w-4 h-4" /> Iniciar</button>
      </div>
    </div>
  </div>
);

const RoleplayModal = ({ scenario, onClose, onSubmit, submitting, learningStyle }) => {
  const [answers, setAnswers]       = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [startTimes, setStartTimes] = useState({});
  const [violations, setViolations] = useState(0);
  const cfg = STYLE_CONFIG[learningStyle] || STYLE_CONFIG.visual;

  const adaptSituation = (s) => {
    if (learningStyle === 'auditory')    return `🎧 Imagina que escuchas esto: "${s}"`;
    if (learningStyle === 'kinesthetic') return `🤝 Situación real: "${s}"`;
    if (learningStyle === 'reading')     return `📋 Contexto: ${s}`;
    return s;
  };
  const getPlaceholder = () => {
    if (learningStyle === 'auditory')    return 'Escribe cómo lo dirías en voz alta...';
    if (learningStyle === 'kinesthetic') return 'Escribe exactamente lo que harías y dirías...';
    if (learningStyle === 'reading')     return 'Redacta tu respuesta estructurada...';
    return 'Escribe tu respuesta aquí...';
  };

  useEffect(() => {
    if (scenario?.steps?.[currentStep]) {
      const stepId = scenario.steps[currentStep].id;
      setStartTimes(p => { if (p[stepId]) return p; return { ...p, [stepId]: Date.now() }; });
    }
  }, [currentStep, scenario?.steps]);

  useEffect(() => {
    const warn = (msg) => { setViolations(v => v + 1); toast.error(msg, { duration: 3000 }); };
    const styleEl = document.createElement('style'); styleEl.id = 'roleplay-anti-cheat-css'; styleEl.innerHTML = ANTI_CHEAT_CSS; document.head.appendChild(styleEl);
    const onContextMenu = (e) => { e.preventDefault(); warn('⚠️ Clic derecho bloqueado'); };
    const onCopy  = (e) => { e.preventDefault(); warn('⚠️ Copiar no permitido'); };
    const onPaste = (e) => { e.preventDefault(); warn('⚠️ Pegar no permitido'); };
    const onVisible = () => { if (document.hidden) warn('⚠️ Saliste de la ventana'); };
    document.addEventListener('contextmenu', onContextMenu); document.addEventListener('copy', onCopy); document.addEventListener('paste', onPaste); document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('contextmenu', onContextMenu); document.removeEventListener('copy', onCopy); document.removeEventListener('paste', onPaste); document.removeEventListener('visibilitychange', onVisible); document.getElementById('roleplay-anti-cheat-css')?.remove(); };
  }, []);

  const step     = scenario.steps[currentStep];
  const progress = ((currentStep + 1) / scenario.steps.length) * 100;
  const handleNext   = () => { if (!answers[step.id]?.trim()) { toast.error('Por favor escribe tu respuesta'); return; } if (currentStep < scenario.steps.length - 1) setCurrentStep(p => p + 1); };
  const handleSubmit = () => {
    if (!answers[step.id]?.trim()) { toast.error('Por favor completa la última respuesta'); return; }
    const responses = scenario.steps.map(s => ({ step_id: s.id, user_response: answers[s.id] || '', time_taken_seconds: Math.floor((Date.now() - (startTimes[s.id] || Date.now())) / 1000) }));
    onSubmit(responses);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md roleplay-active-zone dash-root">
      <style>{GLOBAL_STYLES}</style>
      <div className="bg-[#0a0a0a] rounded-2xl max-w-2xl w-full overflow-hidden relative z-10 flex flex-col max-h-[90vh]"
        style={{ border: `1px solid ${a3bd('30%')}`, boxShadow: `0 0 50px ${a3bg('15%')}` }}>
        <div className="border-b px-6 py-5 flex items-start justify-between"
          style={{ background: `linear-gradient(90deg, ${a3bg('10%')}, transparent)`, borderColor: a3bd('20%') }}>
          <div className="flex gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: a3bg('20%'), border: `1px solid ${a3bd('40%')}` }}>
              <MessageSquare className="w-6 h-6" style={{ color: 'var(--org-accent3, #ccff00)' }} />
            </div>
            <div>
              <h2 className="text-xl font-black text-white tracking-tight">{scenario.title}</h2>
              <p className="text-white/50 text-[11px] uppercase tracking-widest font-bold mt-1 line-clamp-1">{scenario.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1"><X className="w-6 h-6" /></button>
        </div>
        <div className="px-6 py-4 bg-white/5 border-b border-white/5">
          <div className="flex justify-between text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--org-accent3, #ccff00)' }}>
            <span>Paso {currentStep + 1} de {scenario.steps.length}</span><span>{Math.round(progress)}%</span>
          </div>
          <div className="bg-white/10 rounded-full h-1.5 overflow-hidden">
            <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${progress}%`, background: 'var(--org-accent3, #ccff00)', boxShadow: '0 0 10px var(--org-accent3, #ccff00)' }} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest border"
            style={{ background: a1bg('8%'), borderColor: a1bd('25%'), color: 'var(--org-accent, #00FFFF)' }}>
            <cfg.icon className="w-3.5 h-3.5" /> Adaptado: {cfg.label}
          </div>
          {violations > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs font-bold uppercase tracking-widest">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" /><span><strong>⚠️ {violations} INFRACCIÓN{violations !== 1 ? 'ES' : ''}</strong></span>
            </div>
          )}
          <div className="flex items-start gap-4 p-5 rounded-xl" style={{ background: a1bg('5%'), border: `1px solid ${a1bd('20%')}` }}>
            <div className="mt-1"><Brain className="w-5 h-5" style={{ color: 'var(--org-accent, #00FFFF)' }} /></div>
            <div>
              <p className="font-black text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--org-accent, #00FFFF)' }}>Situación Actual:</p>
              <p className="text-white/80 text-[15px] leading-relaxed">{adaptSituation(step.situation)}</p>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-black uppercase tracking-widest mb-3" style={{ color: 'var(--org-accent3, #ccff00)' }}>Tu Respuesta</label>
            <textarea value={answers[step.id] || ''} onChange={(e) => setAnswers(p => ({ ...p, [step.id]: e.target.value }))} rows={5} placeholder={getPlaceholder()} className="cy-input w-full px-4 py-4 rounded-xl text-[15px] resize-none" />
            <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest font-bold">
              <p className="text-white/30">⏱️ Analizando tipeo</p>
              <p style={{ color: 'var(--org-accent3, #ccff00)' }}>{Math.max(0, (answers[step.id] || '').length)} chars</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 border-t border-white/10 bg-black/40 flex items-center justify-between gap-3">
          {currentStep > 0 ? (
            <button onClick={() => setCurrentStep(p => p - 1)} className="px-5 py-3 border border-white/20 text-white rounded-xl hover:bg-white/10 font-bold text-xs uppercase tracking-widest transition-colors">← Volver</button>
          ) : <div />}
          {currentStep < scenario.steps.length - 1 ? (
            <button onClick={handleNext} className="btn-neon-a1 px-8 py-3 rounded-xl text-xs uppercase tracking-widest">Siguiente →</button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} className="btn-neon-a3 px-8 py-3 rounded-xl text-xs uppercase tracking-widest flex items-center gap-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Evaluando...</> : <><Send className="w-4 h-4" /> Enviar Evaluación</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const useModuleTimeTracker = (moduleId, api) => {
  const sessionStartRef = useRef(Date.now()); const intervalRef = useRef(null);
  const flush = useCallback(async () => {
    const sessionSeconds = Math.floor((Date.now() - sessionStartRef.current) / 1000);
    if (sessionSeconds < 5 || !moduleId) return;
    try { await api.post(`/modules/${moduleId}/track-time`, { module_id: moduleId, session_seconds: sessionSeconds }); sessionStartRef.current = Date.now(); } catch {}
  }, [moduleId, api]);
  useEffect(() => {
    if (!moduleId) return;
    sessionStartRef.current = Date.now();
    intervalRef.current = setInterval(flush, 30000);
    const onUnload = () => flush(); window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', () => { if (document.hidden) flush(); });
    return () => { clearInterval(intervalRef.current); window.removeEventListener('beforeunload', onUnload); flush(); };
  }, [moduleId, flush]);
  return { flush };
};

const formatTime = (seconds) => {
  if (!seconds) return '0 min'; const h = Math.floor(seconds / 3600); const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`; return `${m} min`;
};

const useMiniBot = (moduleTitle, isTestMode) => {
  const [open, setOpen]     = useState(false); const [messages, setMessages] = useState([]); const [input, setInput] = useState(''); const [loading, setLoading] = useState(false); const bottomRef = useRef(null);
  const isTestModeRef = useRef(isTestMode); const moduleTitleRef = useRef(moduleTitle);
  useEffect(() => {
    if (open && messages.length === 0) {
      const tm = isTestModeRef.current; const ti = moduleTitleRef.current;
      setMessages([{ role: 'assistant', text: tm ? `Sistema de ayuda inicializado. Evaluando entorno de test para "${ti}". ¿Necesitas una pista conceptual?` : `¡Hola! Soy tu asistente IA para el módulo "${ti}". ¿En qué concepto te puedo ayudar?` }]);
    }
  }, [open, messages.length]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim(); setInput(''); setMessages(p => [...p, { role: 'user', text: userMsg }]); setLoading(true);
    try {
      const systemContext = isTestMode
        ? `Ayudas a un estudiante durante un TEST sobre "${moduleTitle}". NUNCA des la respuesta directamente. Sí puedes dar pistas conceptuales. Sé breve.`
        : `Eres tutor del módulo "${moduleTitle}". Responde dudas con claridad y ejemplos prácticos.`;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ai/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ message: userMsg, context: systemContext }),
      });
      const data = await res.json();
      setMessages(p => [...p, { role: 'assistant', text: data.response || 'No pude procesar tu consulta.' }]);
    } catch { setMessages(p => [...p, { role: 'assistant', text: 'Error de conexión. Reinténtalo.' }]); }
    finally { setLoading(false); }
  };
  return { open, setOpen, messages, input, setInput, send, loading, bottomRef };
};

const MiniBotWidget = ({ moduleTitle, isTestMode, locked }) => {
  const { open, setOpen, messages, input, setInput, send, loading, bottomRef } = useMiniBot(moduleTitle, isTestMode);
  const accentVar = isTestMode ? 'var(--org-accent2, #FF00FF)' : 'var(--org-accent, #00FFFF)';
  const accentBg  = isTestMode ? a2bg('10%') : a1bg('10%');
  const accentBd  = isTestMode ? a2bd('30%') : a1bd('30%');
  return (
    <>
      <button onClick={() => setOpen(p => !p)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 border-2"
        style={{ background: accentBg, borderColor: accentVar, color: accentVar, boxShadow: `0 0 20px ${accentBg}` }}>
        {open ? <X className="w-6 h-6" /> : <Brain className="w-6 h-6" />}
        {isTestMode && !open && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-black shadow-[0_0_10px_red] animate-pulse">!</span>}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 bg-[#060810]/95 backdrop-blur-xl rounded-2xl border flex flex-col overflow-hidden" style={{ height: '450px', borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="px-4 py-3 flex items-center gap-3 border-b" style={{ background: accentBg, borderColor: accentBd }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ background: accentBg, borderColor: accentBd }}>
              <Brain className="w-4 h-4" style={{ color: accentVar }} />
            </div>
            <div className="flex-1">
              <p className="font-black text-xs uppercase tracking-widest" style={{ color: accentVar }}>Terminal IA</p>
              <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">{isTestMode ? 'Supervisión Activa' : 'Asistencia en Línea'}</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>

          {locked ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center bg-black/40">
              <Sparkles className="w-8 h-8" style={{ color: accentVar }} />
              <p className="font-black text-sm uppercase tracking-widest" style={{ color: accentVar }}>Pásate al plan PLUS</p>
              <p className="text-white/60 text-xs leading-relaxed">
                El asistente IA no está incluido en tu plan. Habla con el administrador de tu empresa para activarlo.
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/40">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mr-2 mt-1 border" style={{ background: accentBg, borderColor: accentBd }}>
                        <Brain className="w-3 h-3" style={{ color: accentVar }} />
                      </div>
                    )}
                    <div className="max-w-[85%] px-3.5 py-2.5 rounded-xl text-[13px] leading-relaxed"
                      style={msg.role === 'user'
                        ? { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px 12px 4px 12px' }
                        : { background: accentBg, border: `1px solid ${accentBd}`, borderRadius: '12px 12px 12px 4px', color: '#fff' }}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mr-2 border" style={{ background: accentBg, borderColor: accentBd }}>
                      <Brain className="w-3 h-3" style={{ color: accentVar }} />
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-xl rounded-tl-sm px-4 py-3">
                      <div className="flex gap-1.5 items-center h-2">
                        {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: accentVar, animationDelay: `${d}ms` }} />)}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-white/10 bg-black/60">
                {isTestMode && <p className="text-[9px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ color: accentVar }}><ShieldAlert className="w-3 h-3" /> Solo asistencia — Respuestas directas bloqueadas</p>}
                <div className="flex gap-2">
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Transmite tu consulta..." className="cy-input flex-1 px-3 py-2 text-[13px] rounded-lg outline-none" />
                  <button onClick={send} disabled={!input.trim() || loading} className="btn-neon-a1 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 disabled:opacity-40">
                    <Send className="w-4 h-4 text-black" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

/* ─── Componente principal ──────────────────────────────────────────────────── */
const ModuleViewer = () => {
const { id } = useParams(); const navigate = useNavigate(); 
const location = useLocation(); const { api, user } = useAuth();  
const learningStyle = useLearningStyle(user);
const canTutor = !!user?.features?.ai_tutor;
const canRoleplay = !!user?.features?.roleplay;
  const [module, setModule]         = useState(null);
  const [tests, setTests]           = useState([]);
  const [userProgress, setUserProgress] = useState({ sections: {} });
  const [subModulesProgress, setSubModulesProgress] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading]       = useState(true);
  const [viewingSection, setViewingSection] = useState(null);
  const [marking, setMarking]       = useState(false);
  const [showingRoleplay, setShowingRoleplay] = useState(false);
  const [submittingRoleplay, setSubmittingRoleplay] = useState(false);
  const [pendingTest, setPendingTest] = useState(null);
  const [activeTest, setActiveTest] = useState(null);
  const [testAnswers, setTestAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [violations, setViolations] = useState(0);
  const [timeData, setTimeData]     = useState({ total_seconds: 0, required_seconds: 0, percentage: 100, unlocked: true });
  const [showDiploma, setShowDiploma] = useState(false);
  const [diplomaDate, setDiplomaDate] = useState(null);
  const [progressVersion, setProgressVersion] = useState(0);
  const [moduleFlags, setModuleFlags] = useState({
  require_screen_recording: true,
  require_time_tracking: true,
});
  const screenRecordingRequired = module?.requires_screen_record !== false;
  const { recording, permission, stopAndUpload } = useScreenRecording({
  active: !!activeTest && moduleFlags.require_screen_recording,
  testId: activeTest?.id,
  moduleId: id,
  api
});
  const { flush: flushTime } = useModuleTimeTracker(id, api);

  useEffect(() => { loadModuleData(); }, [id]);

  useEffect(() => {
    if (!activeTest) return;
    const warn = (msg) => { setViolations(v => v + 1); toast.error(msg, { duration: 3000 }); };
    const styleEl = document.createElement('style'); styleEl.id = 'anti-cheat-css'; styleEl.innerHTML = ANTI_CHEAT_CSS; document.head.appendChild(styleEl);
    const onContextMenu = (e) => { e.preventDefault(); warn('⚠️ Clic derecho bloqueado'); };
    const onKeyDown = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (e.key === 'PrintScreen' || e.key === 'F12' || (ctrl && ['c','a','p','s','u','v'].includes(e.key.toLowerCase()))) { e.preventDefault(); e.stopPropagation(); warn('⚠️ Atajo bloqueado'); return false; }
    };
    const onCopy    = (e) => { e.preventDefault(); warn('⚠️ Copiar no permitido'); };
    const onPaste   = (e) => e.preventDefault();
    const onVisible = () => { if (document.hidden) warn('⚠️ Salida de ventana detectada'); };
    document.addEventListener('contextmenu', onContextMenu); document.addEventListener('keydown', onKeyDown, true); document.addEventListener('copy', onCopy); document.addEventListener('paste', onPaste); document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu); document.removeEventListener('keydown', onKeyDown, true); document.removeEventListener('copy', onCopy); document.removeEventListener('paste', onPaste); document.removeEventListener('visibilitychange', onVisible);
      document.getElementById('anti-cheat-css')?.remove();
    };
  }, [activeTest]);

  const loadModuleData = async () => {
    try {
      setLoading(true);
      const [modR, testR, progR, timeR] = await Promise.all([api.get(`/modules/${id}`), api.get(`/modules/${id}/tests`), api.get(`/modules/${id}/my-progress`), api.get(`/modules/${id}/my-time`)]);
      setModule(modR.data); 
      setProgressVersion(v => v + 1);
      setTests(testR.data || []); setUserProgress({ sections: {}, ...(progR.data || {}) }); setTimeData(timeR.data);
      setModuleFlags({
  require_screen_recording: modR.data.requires_screen_record !== false,
  require_time_tracking:    modR.data.require_time_tracking    !== false,
});
if (modR.data?.has_submodules) {
  try {
    const subsR = await api.get(`/modules/${id}/submodules`);
    setSubModulesProgress(subsR.data || []);
    const allDone = subsR.data?.length > 0 && subsR.data.every(sm => sm.user_progress?.test_passed === true);
    if (allDone && canRoleplay && modR.data?.roleplay_scenario && location?.state?.openRoleplay) {
      setTimeout(() => setShowingRoleplay(true), 600);
    }
  } catch {
    setSubModulesProgress([]);
  }
}    } catch { toast.error('Error de red al cargar el módulo'); }
    finally { setLoading(false); }
  };

  const parseModuleSections = (content) => {
    if (!content) return [];
    const sections = []; const lines = content.split('\n'); let cur = null; let buf = [];
    lines.forEach(line => {
      if (line.trim().startsWith('##') && !line.includes('###')) {
        if (cur) { cur.content = buf.join('\n').trim(); sections.push(cur); }
        cur = { id: `section_${sections.length + 1}`, title: line.replaceAll(/^##\s*/g, '').trim(), content: '' }; buf = [];
      } else if (cur) buf.push(line);
    });
    if (cur) { cur.content = buf.join('\n').trim(); sections.push(cur); }
    return sections;
  };

  const markSectionComplete = async (sectionId) => {
    setMarking(true);
    try {
      await api.post(`/modules/${id}/mark-section-complete`, { section_id: sectionId });
      toast.success('Sección completada.');
      await loadModuleData();
      setUserProgress(p => ({ ...p, sections: { ...p.sections, [sectionId]: { ...(p.sections?.[sectionId] || {}), completed: true } } }));
      setViewingSection(null);
    } catch { toast.error('Error al sincronizar progreso'); }
    finally { setMarking(false); }
  };

  const confirmStartTest = () => { if (!pendingTest) return; setViolations(0); setActiveTest(randomizeTest(pendingTest)); setTestAnswers({}); setPendingTest(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const cancelTest = async () => { await stopAndUpload(); setActiveTest(null); setTestAnswers({}); };
  const checkDiplomaPopup = async () => {
  if (!module?.diploma_enabled) return;
  try {
    const { data } = await api.get(`/modules/${id}/diploma-eligibility`);
    if (data?.eligible) {
      setDiplomaDate(data.completed_at);
      setTimeout(() => setShowDiploma(true), 800);
    }
  } catch { /* si falla, el botón del header sigue disponible */ }
};
const submitTest = async () => {
  if (!activeTest) return;
  const unanswered = activeTest.questions.filter(q => testAnswers[q.id] === undefined);
  if (unanswered.length > 0) { toast.error(`Responde ${unanswered.length} pregunta(s) pendiente(s)`); return; }
  setSubmitting(true);
  try {
    await stopAndUpload();
    const { data } = await api.post(`/modules/${id}/test-results`, {
      test_id: activeTest.id,
      section_id: activeTest.section_id,
      answers: testAnswers,
      violations,
      screen_recorded: recording || permission === 'granted',
    });
    const pct = data.percentage ?? 0;
    setActiveTest(null);
    setTestAnswers({});
    await loadModuleData();
    if (data.passed) {
      toast.success(`¡Test Aprobado! Precisión: ${pct.toFixed(0)}%`);
      await checkDiplomaPopup();
    } else {
      toast.error(`Test Fallido. Precisión: ${pct.toFixed(0)}% (Req: 70%)`);
    }
  } catch { toast.error('Error en la transmisión de resultados'); }
  finally { setSubmitting(false); }
};

  const submitRoleplay = async (responses) => {
    setSubmittingRoleplay(true);
    try {
      const res = await api.post(`/modules/${id}/roleplay`, { module_id: id, responses });
      if (res.data.passed) {
  toast.success(`¡Roleplay verificado! Puntuación: ${res.data.overall_score}/100`);
  await checkDiplomaPopup();
} else toast.error(`Roleplay no aprobado. ${res.data.feedback || ''}`);
      setShowingRoleplay(false); loadModuleData();
    } catch (err) { toast.error('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmittingRoleplay(false); }
  };

  if (loading) return (
    <div className="dash-root flex items-center justify-center">
      <style>{GLOBAL_STYLES}</style>
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--org-accent, #00FFFF)', filter: 'drop-shadow(0 0 10px var(--org-accent, #00FFFF))' }} />
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--org-accent, #00FFFF)' }}>Cargando Módulo...</span>
      </div>
    </div>
  );

  if (!module) return (
    <div className="dash-root flex items-center justify-center">
      <style>{GLOBAL_STYLES}</style>
      <div className="glass-panel p-10 text-center border-red-500/30">
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3 opacity-80" />
        <p className="text-white font-bold uppercase tracking-widest">Módulo Inaccesible</p>
      </div>
    </div>
  );

  const sections = parseModuleSections(module.content);
  const hasRoleplay = canRoleplay && !!module.roleplay_scenario;
  const timeTrackingRequired = module?.require_time_tracking !== false;
  const timeUnlocked = !moduleFlags.require_time_tracking || (timeData?.unlocked ?? true);
  const cfg = STYLE_CONFIG[learningStyle] || STYLE_CONFIG.visual;

  const allSectionsCompleted = sections.every(s => userProgress.sections?.[s.id]?.completed);
  const allTestsPassed       = tests.every(t => userProgress.sections?.[t.section_id]?.test_passed);
  const allSubModulesDone    = subModulesProgress.length > 0 ? subModulesProgress.every(sm => sm.user_progress?.test_passed === true) : false;
  const roleplayUnlocked = module.has_submodules
    ? allSubModulesDone && timeUnlocked && hasRoleplay
    : allSectionsCompleted && allTestsPassed && timeUnlocked && hasRoleplay;
  const moduleCompleted = module.has_submodules
    ? allSubModulesDone
    : sections.length > 0 && allSectionsCompleted && allTestsPassed;

  /* ── VISTA TEST ─────────────────────────────────────────────────────────── */
  if (activeTest) {
    return (
      <div className="dash-root py-8 test-active-zone" onContextMenu={e => e.preventDefault()}>
        <style>{GLOBAL_STYLES}</style>
        <div className="main-content max-w-4xl mx-auto px-4 z-10 relative">
          <div className="mb-4 flex justify-end">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg border text-[11px] font-black uppercase tracking-widest"
              style={recording ? { background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)', color: '#f87171' } : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
{moduleFlags.require_screen_recording ? (
  recording
    ? <><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Pantalla Activa</>
: <><VideoOff className="w-4 h-4" /> Sin Grabación</>
) : (
  <><VideoOff className="w-4 h-4" /> Grabación desactivada</>
)}            </div>
          </div>
          <div className="glass-panel overflow-hidden" style={{ borderColor: a2bd('30%'), boxShadow: `0 0 40px ${a2bg('10%')}` }}>
            <div className="border-b px-8 py-6 text-white relative"
              style={{ background: `linear-gradient(90deg, ${a2bg('20%')}, transparent)`, borderColor: a2bd('20%') }}>
              <button onClick={cancelTest} className="absolute top-6 right-8 text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1"><X className="w-4 h-4" /> Abortar</button>
              <p className="text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: 'var(--org-accent2, #FF00FF)' }}><ShieldAlert className="w-3.5 h-3.5" /> Evaluación Protocolar</p>
              <h2 className="text-3xl font-black mb-1 tracking-tight">{activeTest.section_title}</h2>
              <p className="text-white/60 text-xs font-bold uppercase tracking-widest">{activeTest.questions.length} preguntas · Umbral: 70%</p>
            </div>
            <div className="px-8 pt-6"><LearningStyleBanner style={learningStyle} /></div>
            <div className="p-8 space-y-8">
              {activeTest.questions.map((q, idx) => (
                <div key={q.id} className="bg-white/5 border border-white/10 rounded-xl p-6 relative">
                  <div className="absolute top-0 right-0 bg-white/10 px-3 py-1 rounded-bl-xl rounded-tr-xl text-[10px] font-black uppercase text-white/50">{q.points} PTS</div>
                  <div className="flex items-start gap-4 mb-6">
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm"
                      style={{ background: a2bg('20%'), border: `1px solid ${a2bd('40%')}`, color: 'var(--org-accent2, #FF00FF)' }}>
                      {idx + 1}
                    </span>
                    <p className="font-bold text-white text-lg leading-snug mt-0.5">{q.question}</p>
                  </div>
                  <div className="space-y-3 ml-12">
                    {q.options.map((opt, oi) => {
                      const isSelected = testAnswers[q.id] === opt.id;
                      return (
                        <label key={opt.id} className="test-option relative flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all"
                          style={isSelected ? { borderColor: a3bd('60%'), background: a3bg('10%'), boxShadow: `0 0 15px ${a3bg('15%')}` } : { borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}
                          >
                            <input type="radio" name={`pregunta-${q.id}`} value={opt.id} checked={isSelected}
                             onChange={() => setTestAnswers(p => ({ ...p, [q.id]: opt.id }))}
                             className="sr-only" />
                          <div className="w-5 h-5 rounded-full border-2  flex items-center justify-center flex-shrink-0 transition-colors"
                            style={{ borderColor: isSelected ? 'var(--org-accent3, #ccff00)' : 'rgba(255,255,255,0.3)' }}>
                            {isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--org-accent3, #ccff00)' }} />}
                          </div>
                          <span className="font-black text-[13px]" style={{ color: isSelected ? 'var(--org-accent3, #ccff00)' : 'rgba(255,255,255,0.4)' }}>{String.fromCharCode(65 + oi)}.</span>
                          <span className="text-[15px]" style={{ color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)', fontWeight: isSelected ? '700' : '400' }}>{opt.text}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="flex gap-4 pt-6 border-t border-white/10">
                <button onClick={cancelTest} className="flex-1 px-6 py-4 border border-white/20 text-white rounded-xl hover:bg-white/10 font-bold text-xs uppercase tracking-widest transition-colors">Abortar</button>
                <button onClick={submitTest} disabled={submitting} className="flex-1 btn-neon-a3 rounded-xl flex items-center justify-center gap-3 text-xs uppercase tracking-widest">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Transmitir Respuestas
                </button>
              </div>
            </div>
          </div>
        </div>
        <MiniBotWidget moduleTitle={module?.title} isTestMode={true}  locked={!canTutor} />
      </div>
    );
  }

  if (pendingTest) return <TestStartModal test={pendingTest} onConfirm={confirmStartTest} onCancel={() => setPendingTest(null)} />;
  if (showingRoleplay && module.roleplay_scenario) {
    return <RoleplayModal scenario={module.roleplay_scenario} onClose={() => setShowingRoleplay(false)} onSubmit={submitRoleplay} submitting={submittingRoleplay} learningStyle={learningStyle} />;
  }

  /* ── Bloque de Roleplay reutilizable ────────────────────────────────────── */
  const RoleplayBlock = () => hasRoleplay ? (
    <div className={`glass-panel overflow-hidden ${!roleplayUnlocked ? 'opacity-60 grayscale-[50%]' : ''}`}
      style={roleplayUnlocked ? { borderColor: a2bd('25%'), boxShadow: `0 0 20px ${a2bg('8%')}` } : {}}>
      <div className="px-6 py-5 border-b" style={{ background: `linear-gradient(90deg, ${a2bg('15%')}, transparent)`, borderColor: a2bd('10%') }}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: a2bg('20%'), border: `1px solid ${a2bd('40%')}` }}>
            <MessageSquare className="w-5 h-5" style={{ color: 'var(--org-accent2, #FF00FF)' }} />
          </div>
          <div>
            <h2 className="text-xl font-black text-white tracking-tight">Simulador Interactivo</h2>
            <p className="text-white/60 text-[11px] uppercase tracking-widest font-bold mt-1">{module.roleplay_scenario.title}</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <p className="text-white/80 text-[14px] leading-relaxed">{module.roleplay_scenario.description}</p>
        <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: a1bg('5%'), border: `1px solid ${a1bd('20%')}` }}>
          <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs" style={{ border: `1px solid ${a1bd('40%')}`, background: a1bg('10%'), color: 'var(--org-accent, #00FFFF)' }}>
            {module.roleplay_scenario.steps.length}
          </div>
          <div>
            <p className="font-black text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--org-accent, #00FFFF)' }}>Directiva de Aprendizaje:</p>
            <p className="text-white/90 text-sm">{module.roleplay_scenario.learning_objective}</p>
          </div>
        </div>
        {!roleplayUnlocked && (
          <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
            <Lock className="w-4 h-4 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 text-[11px] font-black uppercase tracking-widest">Acceso Denegado</p>
              <p className="text-white/50 text-[11px] mt-0.5 font-medium">
                {!timeUnlocked ? `Faltan ${formatTime(Math.max(0, (timeData?.required_seconds ?? 0) - (timeData?.total_seconds ?? 0)))} de inmersión.` : 'Completa todos los tests primero.'}
              </p>
            </div>
          </div>
        )}
        <button onClick={() => roleplayUnlocked && setShowingRoleplay(true)} disabled={!roleplayUnlocked}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-xs uppercase tracking-widest font-black transition-all ${roleplayUnlocked ? 'btn-neon-a2' : 'bg-white/5 text-white/30 border border-white/10 cursor-not-allowed'}`}>
          {roleplayUnlocked ? <><Play className="w-4 h-4" /> Iniciar Secuencia</> : <><Lock className="w-4 h-4" /> Bloqueado</>}
        </button>
      </div>
    </div>
  ) : null;

  /* ── VISTA PRINCIPAL ──────────────────────────────────────────────────────── */
  return (
    <div className="dash-root">
      <style>{GLOBAL_STYLES}</style>
      {viewingSection && (
        <ContentModal section={viewingSection} isCompleted={userProgress.sections?.[viewingSection.id]?.completed} marking={marking}
          onClose={() => setViewingSection(null)} onMarkComplete={() => markSectionComplete(viewingSection.id)} learningStyle={learningStyle} api={api} />
      )}
<ModuleDiploma
  open={showDiploma}
  onClose={() => setShowDiploma(false)}
  moduleTitle={module?.title || ''}
  userName={user?.name || ''}
  completedAt={diplomaDate}
  diplomaConfig={module?.diplomaConfig}
/>
      {/* HEADER */}
      <div className="relative z-10 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/my-modules')} className="mb-6 text-[10px] uppercase font-black tracking-widest flex items-center gap-2 transition-colors hover:text-white"
            style={{ color: 'var(--org-accent, #00FFFF)' }}>
            <ArrowRight className="w-3 h-3 rotate-180" /> Sistema Central
          </button>
          <div className="flex flex-col md:flex-row items-start justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-black mb-3 text-white tracking-tight" style={{ textShadow: '0 0 15px var(--org-scan, rgba(0,255,255,0.4))' }}>
                {module.title}
              </h1>
              <p className="text-white/60 text-sm leading-relaxed">{module.description}</p>
            </div>
            <div className="flex flex-col items-end gap-4 min-w-[200px]">
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border"
    style={{ borderColor: a1bd('30%'), background: a1bg('10%'), color: 'var(--org-accent, #00FFFF)' }}>
    <cfg.icon className="w-3.5 h-3.5" />
    <span className="text-[10px] font-black uppercase tracking-widest">Adaptado: {cfg.label}</span>
  </div>
  <DiplomaDownload
    module={module}
    user={user}
    api={api}
    refreshKey={progressVersion}
  />
</div>
            


          </div>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="main-content max-w-7xl mx-auto space-y-6">
        {module.has_submodules ? (
          <div className="space-y-6"><ModuleUnitsPanel moduleId={id} learningStyle={learningStyle} /><RoleplayBlock /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* SIDEBAR */}
            <div className="lg:col-span-4 xl:col-span-3 space-y-4 lg:sticky lg:top-8">
              <div className="glass-panel p-5">
                <h3 className="font-black mb-5 text-[11px] uppercase tracking-widest flex items-center gap-2" style={{ color: 'var(--org-accent, #00FFFF)' }}>
                  <BookOpen className="w-4 h-4" /> Índice de Datos
                </h3>
                <div className="space-y-3">
                  {sections.map((section, idx) => {
                    const sectionTest = tests.find(t => t.section_id === section.id);
                    const done        = userProgress.sections?.[section.id]?.completed;
                    const testPassed  = userProgress.sections?.[section.id]?.test_passed;
                    return (
                      <div key={section.id} className="bg-black/20 rounded-xl border border-white/5 overflow-hidden">
                        <button onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))}
                          className="w-full flex items-center gap-3 px-4 py-3 font-bold text-xs uppercase tracking-widest transition-colors"
                          style={{ color: done ? 'var(--org-accent3, #84cc16)' : 'rgba(255,255,255,0.85)' }}>
                          {expandedSections[section.id] ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                          <span>Bloque {idx + 1}</span>
                          {done && <CheckCircle className="w-3.5 h-3.5 ml-auto" />}
                        </button>
                        {expandedSections[section.id] && (
                          <div className="p-2 space-y-1 bg-black/40 border-t border-white/5">
                            <button onClick={() => setViewingSection(section)} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-left">
                              {done ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--org-accent3, #84cc16)' }} /> : <Circle className="w-3.5 h-3.5 opacity-30 flex-shrink-0" />}
                              <span className="truncate leading-tight">{section.title}</span>
                            </button>
                            {sectionTest && (
                              <button onClick={() => done && setPendingTest(sectionTest)} disabled={!done || !timeUnlocked}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-xs rounded-lg text-left transition-colors"
                                style={{ color: !done || !timeUnlocked ? 'rgba(255,255,255,0.2)' : testPassed ? 'var(--org-accent3, #ccff00)' : 'var(--org-accent2, #FF00FF)', cursor: !done || !timeUnlocked ? 'not-allowed' : 'pointer' }}>
                                {testPassed ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <FileText className="w-3.5 h-3.5 flex-shrink-0" />}
                                <span className="font-bold uppercase tracking-widest">Test Eval</span>
                                {(!done || !timeUnlocked) && <Lock className="w-3 h-3 ml-auto opacity-50" />}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {hasRoleplay && (
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <button onClick={() => roleplayUnlocked && setShowingRoleplay(true)} disabled={!roleplayUnlocked}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all"
                      style={roleplayUnlocked ? { background: a2bg('20%'), color: 'var(--org-accent2, #FF00FF)', border: `1px solid ${a2bd('40%')}` } : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.05)', cursor: 'not-allowed' }}>
                      <MessageSquare className="w-4 h-4" /><span>Simulador</span>
                      {!roleplayUnlocked && <Lock className="w-3.5 h-3.5 ml-auto" />}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ÁREA PRINCIPAL */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-6">
              {/* Barra de tiempo */}
              {timeData.required_seconds > 0 && (
                <div className="glass-panel p-5 flex flex-col md:flex-row items-center justify-between gap-4"
                  style={timeData.unlocked ? { borderColor: a3bd('40%'), boxShadow: `0 0 20px ${a3bg('8%')}` } : { borderColor: 'rgba(249,115,22,0.4)', boxShadow: '0 0 20px rgba(249,115,22,0.1)' }}>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border"
                      style={timeData.unlocked ? { background: a3bg('20%'), borderColor: a3bd('50%'), color: 'var(--org-accent3, #84cc16)' } : { background: 'rgba(249,115,22,0.2)', borderColor: 'rgba(249,115,22,0.5)', color: '#fb923c' }}>
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: timeData.unlocked ? 'var(--org-accent3, #84cc16)' : '#fb923c' }}>
                        {timeData.unlocked ? 'Requisito Temporal Completado' : 'Requisito de Inmersión'}
                      </p>
                      <p className="text-white font-bold text-sm">{formatTime(timeData.total_seconds)} <span className="text-white/30 font-normal">/ {formatTime(timeData.required_seconds)}</span></p>
                    </div>
                  </div>
                  <div className="w-full md:w-1/2 lg:w-1/3">
                    <div className="bg-black/50 rounded-full h-2 overflow-hidden border border-white/5">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(timeData.percentage, 100)}%`, background: timeData.unlocked ? 'var(--org-accent3, #84cc16)' : '#fb923c', boxShadow: timeData.unlocked ? `0 0 10px var(--org-accent3, #84cc16)` : '0 0 10px #fb923c' }} />
                    </div>
                    {!timeData.unlocked && <p className="text-[10px] mt-1.5 uppercase tracking-widest font-bold text-right text-orange-400/80">Faltan {formatTime(timeData.required_seconds - timeData.total_seconds)}</p>}
                  </div>
                </div>
              )}

              {/* Secciones */}
              <div className="space-y-4">
                {sections.map((section, idx) => {
                  const sectionTest   = tests.find(t => t.section_id === section.id);
                  const isExpanded    = expandedSections[section.id] !== false;
                  const done          = userProgress.sections?.[section.id]?.completed;
                  const testPassed    = userProgress.sections?.[section.id]?.test_passed;
                  const testAttempted = userProgress.sections?.[section.id]?.test_attempted;
                  return (
                    <div key={section.id} className="glass-panel overflow-hidden transition-all">
                      <button type="button" aria-expanded={isExpanded}
  onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))}
  className="w-full text-left flex items-center justify-between p-5 cursor-pointer hover:bg-white/5 transition-colors border-b border-white/5">
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
                            style={done ? { background: a3bg('10%'), borderColor: a3bd('30%'), color: 'var(--org-accent3, #84cc16)' } : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}>
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: 'var(--org-accent, #00FFFF)' }}>Bloque {idx + 1}</p>
                            <h2 className="text-lg font-bold text-white">{section.title}</h2>
                          </div>
                        </div>
                        {done && (
                          <span className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-black uppercase tracking-widest"
                            style={{ background: a3bg('15%'), borderColor: a3bd('30%'), color: 'var(--org-accent3, #84cc16)', border: `1px solid ${a3bd('30%')}` }}>
                            <CheckCircle className="w-3.5 h-3.5" /> Procesado
                          </span>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="p-5 space-y-4 bg-black/20">
                          {/* Contenido */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-xl border"
                            style={done ? { background: a3bg('5%'), borderColor: a3bd('20%') } : { background: a1bg('5%'), borderColor: a1bd('20%') }}>
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border"
                              style={done ? { background: a3bg('20%'), borderColor: a3bd('40%'), color: 'var(--org-accent3, #84cc16)' } : { background: a1bg('20%'), borderColor: a1bd('40%'), color: 'var(--org-accent, #00FFFF)' }}>
                              {done ? <CheckCircle className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-white text-[15px] truncate mb-1">Archivo de Texto</p>
                              <p className="text-[11px] uppercase tracking-widest font-black" style={{ color: done ? 'var(--org-accent3, #84cc16)' : 'rgba(255,255,255,0.4)' }}>
                                {done ? 'Lectura confirmada' : `Modo: ${cfg.label}`}
                              </p>
                            </div>
                            <button onClick={() => setViewingSection(section)}
                              className={`w-full sm:w-auto px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest font-black whitespace-nowrap transition-all ${done ? 'bg-white/10 hover:bg-white/20 text-white' : 'btn-neon-a1'}`}>
                              {done ? 'Repasar' : 'Acceder'}
                            </button>
                          </div>
                          {/* Test */}
                          {sectionTest && (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-xl border"
                              style={!done ? { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.05)', opacity: 0.6 }
                                : testPassed ? { background: a3bg('5%'), borderColor: a3bd('20%') }
                                : { background: a2bg('5%'), borderColor: a2bd('20%') }}>
                              <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border"
                                style={!done ? { background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.3)' }
                                  : testPassed ? { background: a3bg('20%'), borderColor: a3bd('40%'), color: 'var(--org-accent3, #ccff00)' }
                                  : { background: a2bg('20%'), borderColor: a2bd('40%'), color: 'var(--org-accent2, #FF00FF)' }}>
                                {testPassed ? <Award className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-white text-[15px] mb-1">Evaluación de Bloque</p>
                                <p className="text-[10px] uppercase tracking-widest font-black text-white/50 leading-relaxed">
                                  {!done ? 'Requiere lectura previa'
                                    : testPassed ? <span style={{ color: 'var(--org-accent3, #ccff00)' }}>Aprobado</span>
                                    : !timeUnlocked ? <span style={{ color: '#fb923c' }}>Restricción temporal</span>
                                    : testAttempted ? <span className="text-red-400">Fallido — Reintentable</span>
                                    : <span style={{ color: 'var(--org-accent2, #FF00FF)' }}>{sectionTest.questions.length} preguntas · Supervisión</span>}
                                </p>
                              </div>
                              <button onClick={() => done && setPendingTest(sectionTest)} disabled={!done || (!timeUnlocked && !testPassed)}
                                className={`w-full sm:w-auto flex justify-center items-center gap-2 px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest font-black whitespace-nowrap transition-all ${!done || (!timeUnlocked && !testPassed) ? 'bg-white/5 text-white/20 cursor-not-allowed' : testPassed ? 'bg-white/10 hover:bg-white/20 text-white' : 'btn-neon-a2'}`}>
                                {!done || (!timeUnlocked && !testPassed) ? <Lock className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                                {testPassed ? 'Repetir' : 'Iniciar Test'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <RoleplayBlock />
                {sections.length === 0 && (
                  <div className="glass-panel p-16 text-center border-white/5 border-dashed">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-white/20" />
                    <p className="text-white/40 uppercase tracking-widest text-xs font-bold">Sin secciones estructuradas en este módulo.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <MiniBotWidget moduleTitle={module?.title} isTestMode={false} locked={!canTutor} />
    </div>
  );
};

const Badge = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${className}`}>{children}</span>
);

export default ModuleViewer;
